// API Constants
export const API_ENDPOINTS = {
    // Authentication
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    REFRESH_TOKEN: '/auth/refresh-token',
    PROFILE: '/auth/profile',
    CHANGE_PASSWORD: '/auth/change-password',
    FIRST_LOGIN_PASSWORD_CHANGE: '/auth/first-login-password-change',

    // Users
    USERS: '/users',
    USER_BY_ID: (id) => `/users/${id}`,

    // Employees
    EMPLOYEES: '/users', // Using users endpoint as per backend
    EMPLOYEE_BY_ID: (id) => `/users/${id}`,

    // Departments
    DEPARTMENTS: '/departments',
    DEPARTMENT_BY_ID: (id) => `/departments/${id}`,
    DEPARTMENT_EMPLOYEES: (id) => `/departments/${id}/employees`,

    // Leaves
    LEAVES: '/leaves',
    LEAVE_BY_ID: (id) => `/leaves/${id}`,
    LEAVE_TYPES: '/leaves/types',
    LEAVE_STATUS: (id) => `/leaves/${id}/status`,
    LEAVE_APPROVALS: '/leaves/approvals',
    LEAVE_APPROVAL_STATISTICS: '/leaves/approval-statistics',
    MY_LEAVES: '/leaves/my',
    LEAVE_BALANCE: '/leaves/balance',
    LEAVE_BALANCE_BY_USER: (userId) => `/leaves/balance/${userId}`,
    LEAVE_CHECK_BALANCE: '/leaves/check-balance',

    // Removed duplicate LEAVE_BALANCE since it was already defined earlier
    // Removed duplicate LEAVE_BALANCE_BY_USER endpoint since it was defined earlier
    LEAVE_STATISTICS: '/leaves/statistics',
    LEAVE_CHECK_CONFLICTS: '/leaves/check-conflicts',
    LEAVE_BULK_ACTION: '/leaves/bulk-action',
    LEAVE_EXPORT: '/leaves/export',
    LEAVE_CANCEL: (id) => `/leaves/${id}/cancel`,
    LEAVE_ATTENDANCE: (id) => `/leaves/${id}/attendance`,
    LEAVE_DETAILS_READ: 'LeaveDetails:read',
    LEAVE_DETAILS_UPDATE: 'LeaveDetails:update',

    // Attendance
    ATTENDANCE: '/attendance',
    ATTENDANCE_CLOCK_IN: '/attendance/clock-in',
    ATTENDANCE_CLOCK_OUT: '/attendance/clock-out',
    ATTENDANCE_MY: '/attendance/my-attendance',
    ATTENDANCE_EMPLOYEE: (id) => `/attendance/employees/${id}`,
    ATTENDANCE_ALL_EMPLOYEES: '/attendance/employees',
    ATTENDANCE_DEPARTMENT_REPORT: (id) => `/attendance/departments/${id}/report`,
    ATTENDANCE_HOLIDAYS: '/attendance/holidays',
    ATTENDANCE_WORK_SCHEDULE: '/attendance/work-schedule',

    // NEW: Attendance-based Timesheet Management
    ATTENDANCE_TIMESHEET_SUBMIT: '/attendance/timesheet/submit',
    ATTENDANCE_TIMESHEET_TEAM: '/attendance/timesheet/team',
    ATTENDANCE_TIMESHEET_APPROVE: '/attendance/timesheet/approve',
    ATTENDANCE_TIMESHEET_REJECT: '/attendance/timesheet/reject',
    ATTENDANCE_TIMESHEET_PAYROLL: '/attendance/timesheet/payroll',

    // Manual Clock-in
    ATTENDANCE_MANUAL_CLOCK_IN: '/attendance/manual-clock-in',
    ATTENDANCE_DEVICE_LOGS: '/attendance/device-logs',

    // Weekly Attendance
    ATTENDANCE_WEEKLY: '/attendance/weekly',

    // Documents
    DOCUMENTS: '/documents',
    DOCUMENTS_MY: '/documents/my',
    DOCUMENTS_USER: (id) => `/documents/user/${id}`,
    DOCUMENTS_UPLOAD: '/documents/upload',
    DOCUMENTS_DOWNLOAD: (id) => `/documents/${id}/download`,

    // Roles & Permissions
    ROLES: '/roles',
    ROLE_BY_ID: (id) => `/roles/${id}`,
    PERMISSIONS: '/roles/permissions', // Legacy endpoint
    ROLE_ASSIGN: '/roles/assign',
    ROLE_MENU_PERMISSIONS: (id) => `/roles/${id}/menu-permissions`,
    
    // New Permission System Endpoints
    PERMISSIONS_NEW: '/permissions',
    PERMISSIONS_BY_MENU: (menuId) => `/permissions/menus/${menuId}`,
    PERMISSIONS_BY_ROLE: (roleId) => `/permissions/roles/${roleId}`,
    PERMISSIONS_UPDATE_ROLE: (roleId) => `/permissions/roles/${roleId}`,
    PERMISSIONS_CREATE_MENU: (menuId) => `/permissions/menus/${menuId}`,

    // Menus
    MENUS: '/menus',
    MENU_BY_ID: (id) => `/menus/${id}`,
    USER_MENUS: '/menus/user',
    ROLE_MENU_ACCESS: (roleId, menuId) => `/menus/roles/${roleId}/menus/${menuId}/access`,
    MENU_PERMISSION_MATRIX: '/menus/permission-matrix',
    MENU_ORDER_UPDATE: (id) => `/menus/${id}/order`,
    MENU_BULK_PERMISSIONS: '/menus/bulk-permissions',
    MENU_ROLE_PERMISSIONS: (menuId) => `/menus/${menuId}/role-permissions`,

    // **NEW ENDPOINTS - Payroll Management**
    PAYROLL: '/payroll',
    PAYROLL_BY_ID: (id) => `/payroll/${id}`,
    PAYROLL_EMPLOYEES: '/payroll/employees',
    PAYROLL_PROCESS: '/payroll/process',
    PAYROLL_SUBMIT: '/payroll/submit',
    PAYROLL_APPROVE: '/payroll/approve',
    PAYROLL_REJECT: '/payroll/reject',
    PAYROLL_STATUS: '/payroll/status',
    PAYROLL_EMPLOYEE: (userId) => `/payroll/employee/${userId}`,
    PAYROLL_PERIOD: (year, month) => `/payroll/period/${year}/${month}`,
    SALARY_COMPONENTS: '/salary-components',
    SALARY_COMPONENT_BY_USER: (userId) => `/salary-components/user/${userId}`,
    PAYROLL_REPORTS: '/payroll/reports',
    PAYSLIPS: '/payroll/payslips',
    PAYSLIP_BY_ID: (id) => `/payroll/payslips/${id}`,
    PAYROLL_GENERATE: 'Payroll:generate',

    // **NEW ENDPOINTS - Performance Management**
    PERFORMANCE: '/appraisals',
    APPRAISALS: '/appraisals',
    APPRAISAL_BY_ID: (id) => `/appraisals/${id}`,
    APPRAISALS_MY: '/appraisals/my',
    APPRAISALS_PENDING_REVIEW: '/appraisals/pending-review',
    APPRAISALS_STATS: '/appraisals/stats',
    APPRAISALS_EXPORT: '/appraisals/export',
    APPRAISALS_SUBMIT: (id) => `/appraisals/${id}/submit`,

    OBJECTIVES: '/appraisals/objectives',
    OBJECTIVE_BY_ID: (id) => `/appraisals/objectives/${id}`,
    OBJECTIVES_MY: '/appraisals/objectives/my',
    OBJECTIVES_PENDING_APPROVAL: '/appraisals/objectives/pending-approval',
    OBJECTIVES_STATS: '/appraisals/objectives/stats',

    // **NEW ENDPOINTS - Meeting Management**
    MEETINGS: '/meetings',
    MEETING_BY_ID: (meetingId) => `/meetings/${meetingId}`,
    MEETING_STATUS: (meetingId) => `/meetings/${meetingId}/status`,

    // Meeting Attendees
    MEETING_ATTENDEES: (meetingId) => `/meetings/${meetingId}/attendees`,
    MEETING_ATTENDEE_STATUS: (meetingId, attendeeId) => `/meetings/${meetingId}/attendees/${attendeeId}/status`,

    // Meeting Tasks
    ALL_MEETING_TASKS: '/meetings/tasks',
    MEETING_TASKS: (meetingId) => `/meetings/${meetingId}/tasks`,
    MEETING_TASK_BY_ID: (taskId) => `/meetings/tasks/${taskId}`,
    MEETING_TASK_PROGRESS: (taskId) => `/meetings/tasks/${taskId}/progress`,

    // Meeting Documents & Minutes
    MEETING_DOCUMENTS: (meetingId) => `/meetings/${meetingId}/documents`,
    MEETING_MINUTES: (meetingId) => `/meetings/${meetingId}/minutes`,
    MEETING_NOTIFICATIONS: (meetingId) => `/meetings/${meetingId}/notifications`,

    // Meeting Reports & Statistics
    MEETING_REPORTS: '/meetings/reports',
    MEETING_STATISTICS: '/meetings/statistics',
    USER_MEETINGS: '/meetings/user',

    // Meeting Actions (these might use the same endpoints as status updates)
    START_MEETING: (meetingId) => `/meetings/${meetingId}/start`,
    END_MEETING: (meetingId) => `/meetings/${meetingId}/end`,
    CANCEL_MEETING: (meetingId) => `/meetings/${meetingId}/cancel`,

    // **NEW ENDPOINTS - Travel & Advances**
    TRAVEL_REQUESTS: '/travel/requests',
    TRAVEL_REQUEST_BY_ID: (id) => `/travel/requests/${id}`,
    TRAVEL_ADVANCES: '/travel/advances',
    TRAVEL_ADVANCE_BY_ID: (id) => `/travel/advances/${id}`,
    EXPENSE_REPORTS: '/travel/expense-reports',
    EXPENSE_REPORT_BY_ID: (id) => `/travel/expense-reports/${id}`,
    FLAT_RATES: '/travel/flat-rates',
    TRAVEL_APPROVE: (id) => `/travel/requests/${id}/approve`,
    TRAVEL_RETIRE: (id) => `/travel/advances/${id}/retire`,
    TRAVEL_REQUEST_TYPES: '/travel-request-types',
    TRAVEL_REQUEST_TYPE_BY_ID: (id) => `/travel-request-types/${id}`,
    TRAVEL_REQUEST_TYPE_TOGGLE_STATUS: (id) => `/travel-request-types/${id}/toggle-status`,

    // **NEW ENDPOINTS - Petty Cash Management**
    PETTY_CASH: '/petty-cash',
    PETTY_CASH_BOOK: '/petty-cash/book',
    PETTY_CASH_BOOK_BY_ID: (id) => `/petty-cash/book/${id}`,
    PETTY_CASH_EXPENSES: '/petty-cash/expenses',
    PETTY_CASH_EXPENSE_BY_ID: (id) => `/petty-cash/expenses/${id}`,
    REPLENISHMENT_REQUESTS: '/petty-cash/replenishment',
    REPLENISHMENT_REQUEST_BY_ID: (id) => `/petty-cash/replenishment/${id}`,
    REPLENISHMENT_APPROVE: (id) => `/petty-cash/replenishment/${id}/approve`,

    // **NEW ENDPOINTS - Procurement & Suppliers**
    SUPPLIERS: '/procurement/suppliers',
    SUPPLIER_BY_ID: (id) => `/procurement/suppliers/${id}`,
    QUOTATIONS: '/procurement/quotations',
    QUOTATION_BY_ID: (id) => `/procurement/quotations/${id}`,
    PURCHASE_ORDERS: '/procurement/purchase-orders',
    PURCHASE_ORDER_BY_ID: (id) => `/procurement/purchase-orders/${id}`,
    PURCHASE_REQUESTS: '/procurement/requests',
    PURCHASE_REQUEST_BY_ID: (id) => `/procurement/requests/${id}`,
    QUOTATION_COMPARE: '/procurement/quotations/compare',
    PURCHASE_REQUEST_APPROVE: (id) => `/procurement/requests/${id}/approve`,
    GENERATE_LPO: (id) => `/procurement/purchase-orders/${id}/lpo`,

    // **NEW ENDPOINTS - Enhanced Budget Management**
    BUDGETS: '/budgets',
    BUDGET_BY_ID: (id) => `/budgets/${id}`,
    BUDGET_PERIODS: '/budgets/periods',
    BUDGET_PERIOD_BY_ID: (id) => `/budgets/periods/${id}`,
    BUDGET_SUBMIT: (id) => `/budgets/${id}/submit`,
    BUDGET_FINANCE_APPROVE: (id) => `/budgets/${id}/finance-approve`,
    BUDGET_ADMIN_APPROVE: (id) => `/budgets/${id}/admin-approve`,
    BUDGET_PERIOD_SUBMIT: (id) => `/budgets/periods/${id}/submit`,
    BUDGET_PERIOD_APPROVE: (id) => `/budgets/periods/${id}/approve`,
    FISCAL_YEARS: '/fiscal-years',
    QUARTERS: '/finance/quarters',

    // **NEW ENDPOINTS - Enhanced Asset Management**
    ASSETS: '/assets',
    ASSET_BY_ID: (id) => `/assets/${id}`,
    ASSET_DISPOSE: (id) => `/assets/${id}/dispose`,
    ASSET_STATISTICS: '/assets/statistics',
    ASSET_MAINTENANCE: '/asset-maintenance',
    ASSET_MAINTENANCE_BY_ID: (id) => `/asset-maintenance/${id}`,
    ASSET_MAINTENANCE_STATUS: (id) => `/asset-maintenance/${id}/status`,

    // **Enhanced Reports**
    REPORTS: '/reports',
    REPORTS_HR: '/reports/hr',
    REPORTS_FINANCE: '/reports/finance',
    REPORTS_PAYROLL: '/reports/payroll',
    REPORTS_ATTENDANCE: '/reports/attendance',
    REPORTS_PROCUREMENT: '/reports/procurement',
    REPORTS_BUDGET: '/reports/budget',
    REPORTS_ASSETS: '/reports/assets',

    // **System Settings**
    SETTINGS: '/settings',
    LEAVE_TYPE_SETTINGS: '/settings/leave-types',
    HOLIDAY_SETTINGS: '/settings/holidays',
    TRAVEL_REQUEST_TYPE_SETTINGS: '/settings/travel-request-types',
    SALARY_SCALE_SETTINGS: '/settings/salary-scale',
    SYSTEM_LOGS: '/settings/logs',
    SYSTEM_BACKUP: '/settings/backup',
    EMAIL_TEMPLATES: '/settings/email-templates',
    NOTIFICATION_SETTINGS: '/settings/notifications',

    // Removed duplicate SYSTEM_LOGS since it was already defined later in the file
    SYSTEM_LOGS_STATS: '/settings/logs/stats',
    SYSTEM_LOGS_RECENT: '/settings/logs/recent',
    SYSTEM_LOGS_EXPORT: '/settings/logs/export',
    SYSTEM_LOGS_SECURITY: '/settings/logs/security',
    SYSTEM_LOGS_AUDIT_TRAIL: '/settings/logs/audit-trail',
    SYSTEM_LOGS_USER_ACTION: '/settings/logs/user-action',
    AUDIT_LOGS: '/audit/logs',

    // Timesheet Management
    TIMESHEETS: '/timesheets',
    TIMESHEET_BY_ID: (id) => `/timesheets/${id}`,
    TIMESHEET_SUBMIT: (id) => `/timesheets/${id}/submit`,
    TIMESHEET_APPROVE: (id) => `/timesheets/${id}/approve`,
    TIMESHEET_REJECT: (id) => `/timesheets/${id}/reject`,
    TEAM_TIMESHEETS: '/timesheets/team',
    TIMESHEET_SUMMARY: '/timesheets/summary',
    TIMESHEET_EXPORT: '/timesheets/export',
    MY_TIMESHEETS: '/timesheets/my',
};

// Authentication Constants
export const AUTH_CONSTANTS = {
    TOKEN_KEY: import.meta.env.VITE_AUTH_TOKEN_KEY || 'ghf_auth_token',
    REFRESH_TOKEN_KEY: import.meta.env.VITE_AUTH_REFRESH_TOKEN_KEY || 'ghf_refresh_token',
    USER_KEY: import.meta.env.VITE_AUTH_USER_KEY || 'ghf_user_data',
    LOGIN_REDIRECT: '/dashboard',
    LOGOUT_REDIRECT: '/login',
};

// Permission Constants
// All permissions now use standardized "Action Module" format to match backend
export const PERMISSIONS = {
    // Users Module
    USERS_READ: 'View Users',
    USERS_CREATE: 'Create User',
    USERS_UPDATE: 'Edit User',
    USERS_DELETE: 'Delete User',
    VIEW_USERS: 'View Users',
    CREATE_USER: 'Create User',
    EDIT_USER: 'Edit User',
    DELETE_USER: 'Delete User',

    // Departments Module
    VIEW_DEPARTMENTS: 'View Departments',
    CREATE_DEPARTMENT: 'Create Department',
    EDIT_DEPARTMENT: 'Edit Department',
    DELETE_DEPARTMENT: 'Delete Department',

    // Menus Module
    MENUS_READ: 'View Menus',
    MENUS_CREATE: 'Create Menu',
    MENUS_UPDATE: 'Edit Menu',
    MENUS_DELETE: 'Delete Menu',
    MANAGE_MENUS: 'Manage Menus',
    MENU_PERMISSIONS: 'Manage Menu Permissions',
    VIEW_MENUS: 'View Menus',
    CREATE_MENU: 'Create Menu',
    EDIT_MENU: 'Edit Menu',
    DELETE_MENU: 'Delete Menu',

    // Leaves Module
    LEAVES_READ: 'View Leaves',
    LEAVES_CREATE: 'Create Leave',
    LEAVES_UPDATE: 'Approve Leaves',
    LEAVES_DELETE: 'Delete Leave',
    VIEW_LEAVES: 'View Leaves',
    CREATE_LEAVE: 'Create Leave',
    APPROVE_LEAVES: 'Approve Leaves',
    DELETE_LEAVE: 'Delete Leave',

    // Attendance Module
    VIEW_ATTENDANCE: 'View Attendance',
    CREATE_ATTENDANCE: 'Create Attendance',
    EDIT_ATTENDANCE: 'Edit Attendance',
    DELETE_ATTENDANCE: 'Delete Attendance',
    VIEW_DEVICE_ATTENDANCE_LOGS: 'View Device Attendance Logs',

    // Documents Module
    DOCUMENTS_READ: 'View Documents',
    DOCUMENTS_CREATE: 'Upload Documents',
    DOCUMENTS_UPDATE: 'Edit Documents',
    DOCUMENTS_DELETE: 'Delete Documents',
    VIEW_DOCUMENTS: 'View Documents',
    UPLOAD_DOCUMENTS: 'Upload Documents',
    EDIT_DOCUMENTS: 'Edit Documents',
    DELETE_DOCUMENTS: 'Delete Documents',

    // Settings Module
    SETTINGS_READ: 'View Settings',
    SETTINGS_CREATE: 'Create Settings',
    SETTINGS_UPDATE: 'Edit Settings',
    SETTINGS_DELETE: 'Delete Settings',
    VIEW_SETTINGS: 'View Settings',
    EDIT_SETTINGS: 'Edit Settings',
    MANAGE_ROLES: 'Manage Roles',
    MANAGE_PERMISSIONS: 'Manage Permissions',
    MANAGE_USERS: 'Manage Users',

    // Finance Module
    FINANCE_READ: 'View Finance',
    FINANCE_CREATE: 'Create Finance Entry',
    FINANCE_UPDATE: 'Edit Finance Entry',
    FINANCE_DELETE: 'Delete Finance Entry',
    VIEW_FINANCE: 'View Finance',
    CREATE_FINANCE_ENTRY: 'Create Finance Entry',
    EDIT_FINANCE_ENTRY: 'Edit Finance Entry',
    DELETE_FINANCE_ENTRY: 'Delete Finance Entry',

    // Payroll
    VIEW_PAYROLL: 'View Payroll',
    PROCESS_PAYROLL: 'Process Payroll',
    APPROVE_PAYROLL: 'Approve Payroll',
    REJECT_PAYROLL: 'Reject Payroll',
    VIEW_PAYROLL_HISTORY: 'View Payroll History',
    VIEW_SALARY_COMPONENTS: 'View Salary Components',

    VIEW_PETTY_CASH: 'View Petty Cash',

    // Budget Management Permissions
    CREATE_BUDGET_PERIOD: 'Create Budget Period',
    SUBMIT_BUDGET_PERIOD: 'Submit Budget Period',
    APPROVE_BUDGET_PERIOD: 'Approve Budget Period',
    VIEW_BUDGET_PERIODS: 'View Budget Periods',

    CREATE_BUDGET: 'Create Budget',
    SUBMIT_BUDGET: 'Submit Budget',
    FINANCE_APPROVE_BUDGET: 'Finance Approve Budget',
    ADMIN_APPROVE_BUDGET: 'Admin Approve Budget',
    VIEW_BUDGETS: 'View Budgets',
    UPDATE_BUDGET: 'Update Budget',
    DELETE_BUDGET: 'Delete Budget',
    CREATE_PETTY_CASH_ENTRY: 'Create Petty Cash Entry',
    UPDATE_PETTY_CASH: 'Update Petty Cash',
    DELETE_PETTY_CASH_ENTRY: 'Delete Petty Cash Entry',
    REQUEST_REPLENISHMENT: 'Request Replenishment',
    APPROVE_REPLENISHMENT: 'Approve Replenishment',
    VIEW_PETTY_CASH_REPORTS: 'View Petty Cash Reports',
    MANAGE_PETTY_CASH_BOOK: 'Manage Petty Cash Book',
    APPROVE_PETTY_CASH_REPLENISHMENT: 'Approve Petty Cash Replenishment',
    APPROVE_PETTY_CASH_EXPENSES: 'Approve Petty Cash Expenses',
    VIEW_PETTY_CASH_APPROVAL_QUEUE: 'View Petty Cash Approval Queue',
    MANAGE_PETTY_CASH_APPROVAL_WORKFLOW: 'Manage Petty Cash Approval Workflow',
    CREATE_PETTY_CASH_BOOK: 'Create Petty Cash Book',

    // Travel & Advances Permissions
    VIEW_TRAVEL_REQUESTS: 'View Travel Requests',
    CREATE_TRAVEL_REQUESTS: 'Create Travel Requests',
    UPDATE_TRAVEL_REQUESTS: 'Update Travel Requests',
    DELETE_TRAVEL_REQUESTS: 'Delete Travel Requests',
    MANAGE_TRAVEL_REQUESTS: 'Manage Travel Requests',
    VIEW_TRAVEL_ADVANCES: 'View Travel Advances',
    CREATE_TRAVEL_ADVANCES: 'Create Travel Advances',
    UPDATE_TRAVEL_ADVANCES: 'Update Travel Advances',
    DELETE_TRAVEL_ADVANCES: 'Delete Travel Advances',
    MANAGE_TRAVEL_ADVANCES: 'Manage Travel Advances',
    VIEW_EXPENSE_REPORTS: 'View Expense Reports',
    CREATE_EXPENSE_REPORTS: 'Create Expense Reports',
    UPDATE_EXPENSE_REPORTS: 'Update Expense Reports',
    DELETE_EXPENSE_REPORTS: 'Delete Expense Reports',
    MANAGE_EXPENSE_REPORTS: 'Manage Expense Reports',

    // Travel & Advances Approval Permissions
    APPROVE_TRAVEL_ADVANCE_REQUESTS: 'Approve Travel Advance Requests',
    APPROVE_TRAVEL_REQUESTS: 'Approve Travel Requests',
    APPROVE_EXPENSE_REPORTS: 'Approve Expense Reports',
    VIEW_TRAVEL_APPROVAL_QUEUE: 'View Travel Approval Queue',
    MANAGE_TRAVEL_APPROVAL_WORKFLOW: 'Manage Travel Approval Workflow',
    FINANCE_APPROVE_TRAVEL_ADVANCES: 'Finance Approve Travel Advances',
    FINANCE_APPROVE_TRAVEL_REQUESTS: 'Finance Approve Travel Requests',
    FINANCE_APPROVE_EXPENSE_REPORTS: 'Finance Approve Expense Reports',
    ADMIN_APPROVE_TRAVEL_ADVANCES: 'Admin Approve Travel Advances',
    ADMIN_APPROVE_TRAVEL_REQUESTS: 'Admin Approve Travel Requests',
    ADMIN_APPROVE_EXPENSE_REPORTS: 'Admin Approve Expense Reports',
    VIEW_TRAVEL_ADVANCES_NEEDING_PAYMENT: 'travel_advances_needing_payment:view',

    // Reports
    REPORTS_READ: 'Reports:read',

    // Dashboard
    DASHBOARD_READ: 'Dashboard:read',

    // Performance Management
    PERFORMANCE_READ: 'View Performance Appraisals',
    PERFORMANCE_CREATE: 'Create Performance Appraisals',
    PERFORMANCE_UPDATE: 'Edit Performance Appraisals',
    PERFORMANCE_DELETE: 'Delete Performance Appraisals',
    PERFORMANCE_REVIEW: 'Review Performance',
    PERFORMANCE_APPROVE: 'Approve Performance',
    VIEW_PERFORMANCE_APPRAISALS: 'View Performance Appraisals',
    CREATE_PERFORMANCE_APPRAISALS: 'Create Performance Appraisals',
    EDIT_PERFORMANCE_APPRAISALS: 'Edit Performance Appraisals',
    DELETE_PERFORMANCE_APPRAISALS: 'Delete Performance Appraisals',
    VIEW_OBJECTIVES: 'View Objectives',
    MANAGE_OBJECTIVES: 'Manage Objectives',
    APPROVE_OBJECTIVES: 'Approve Objectives',

    // Asset Management Permissions
    VIEW_ASSETS: 'View Assets',
    CREATE_ASSET: 'Create Asset',
    UPDATE_ASSET: 'Update Asset',
    DELETE_ASSET: 'Delete Asset',
    MANAGE_ASSETS: 'finance_assets:create',
    SCHEDULE_MAINTENANCE: 'assets_maintenance:create',
    CALCULATE_DEPRECIATION: 'assets_depreciation:create',

    // Meetings Module
    VIEW_MEETINGS: 'View Meetings',
    CREATE_MEETINGS: 'Create Meetings',
    UPDATE_MEETINGS: 'Update Meetings',
    DELETE_MEETINGS: 'Delete Meetings',
    MANAGE_MEETING_ATTENDEES: 'Manage Meeting Attendees',
    UPLOAD_MEETING_MINUTES: 'Upload Meeting Minutes',
    CREATE_MEETING_TASKS: 'Create Meeting Tasks',
    UPDATE_TASK_STATUS: 'Update Task Status',
    VIEW_MEETING_REPORTS: 'View Meeting Reports',
    MANAGE_MEETINGS: 'Manage Meetings',
    VIEW_MEETING_TASKS: 'View Meeting Tasks',
    MANAGE_MEETING_TASKS: 'Manage Meeting Tasks',
    ASSIGN_MEETING_TASKS: 'Assign Meeting Tasks',

    // Reports Module
    REPORTS_READ: 'View Reports',
    CREATE_REPORTS: 'Create Reports',
    EDIT_REPORTS: 'Edit Reports',
    DELETE_REPORTS: 'Delete Reports',
    VIEW_REPORTS: 'View Reports',

    // Dashboard
    DASHBOARD_READ: 'View Dashboard',
    VIEW_DASHBOARD: 'View Dashboard',

    // Procurement Module
    VIEW_PURCHASE_REQUESTS: 'View Purchase Requests',
    CREATE_PURCHASE_REQUESTS: 'Create Purchase Requests',
    APPROVE_PURCHASE_REQUESTS: 'Approve Purchase Requests',
    VIEW_QUOTATIONS: 'View Quotations',
    CREATE_QUOTATIONS: 'Create Quotations',
    COMPARE_QUOTATIONS: 'Compare Quotations',
    APPROVE_QUOTATIONS: 'Approve Quotations',
    VIEW_PURCHASE_ORDERS: 'View Purchase Orders',
    CREATE_PURCHASE_ORDERS: 'Create Purchase Orders',
    UPDATE_PURCHASE_ORDERS: 'Update Purchase Orders',
    VIEW_PROCUREMENT_REPORTS: 'View Procurement Reports',
    MANAGE_VENDOR_SELECTION: 'Manage Vendor Selection',
    GENERATE_LPO: 'Generate LPO',
    TRACK_DELIVERIES: 'Track Deliveries',
    VIEW_SUPPLIERS: 'View Suppliers',
    MANAGE_SUPPLIERS: 'Manage Suppliers',
    MANAGE_PURCHASE_ORDERS: 'Manage Purchase Orders',
    MANAGE_PURCHASE_REQUESTS: 'Manage Purchase Requests',
    MANAGE_QUOTATIONS: 'Manage Quotations',

    // Fiscal Year & Holidays
    MANAGE_FISCAL_YEAR: 'Manage Fiscal Year',
    MANAGE_HOLIDAYS: 'Manage Holidays',
    MANAGE_LEAVE_TYPES: 'Manage Leave Types',
    MANAGE_SALARY_SCALE: 'Manage Salary Scale',
};

// Role Constants
export const ROLES = {
    ADMIN: 'Admin',
    HR_MANAGER: 'HR Manager',
    DEPARTMENT_HEAD: 'Department Head',
    EMPLOYEE: 'Employee',
    FINANCE_MANAGER: 'Finance Manager',
};

/**
 * Permission strings that grant access to travel routes (advances, approval queue).
 * Matches backend routes/travel.js TRAVEL_ACCESS and DB permission names / module:action.
 * Use hasAnyPermission(TRAVEL_ACCESS_PERMISSIONS) for permission-based access only.
 */
export const TRAVEL_ACCESS_PERMISSIONS = [
    'View Travel Requests',
    'Manage Travel Requests',
    'View Travel Advances',
    'View Travel Approval Queue',
    'Approve Travel Advance Requests',
    'Finance Approve Travel Advances',
    'Admin Approve Travel Advances',
    'travel:view',
    'travel:create',
    'travel:update',
    'travel_requests:view',
    'travel_requests:create',
    'travel_requests:update',
    'travel_advances:view',
    'travel_advances:create',
    'travel_advances:update',
    'Travel:read',
    'Travel:update',
];

// Route Constants
export const ROUTES = {
    // Public routes
    LOGIN: '/login',
    FORGOT_PASSWORD: '/forgot-password',

    // Protected routes
    DASHBOARD: '/dashboard',

    // Employee routes
    EMPLOYEES: '/employees',
    EMPLOYEE_CREATE: '/employees/create',
    EMPLOYEE_DETAILS: '/employees/:id',
    EMPLOYEE_EDIT: '/employees/:id/edit',

    // Leave routes
    LEAVES: '/leaves',
    LEAVE_CREATE: '/leaves/create',
    LEAVE_DETAILS: '/leaves/:id',
    LEAVE_APPROVALS: '/leaves/approvals',
    TIMESHEET: '/attendance/timesheet',
    TIMESHEET_APPROVAL: '/attendance/timesheet/approval',
    PAYROLL_PROCESSING: '/finance/payroll/processing',

    // Attendance routes
    ATTENDANCE: '/attendance',
    ATTENDANCE_MY_RECORDS: '/attendance/my-records',
    ATTENDANCE_EMPLOYEE_RECORDS: '/attendance/employee-records',
    ATTENDANCE_REPORTS: '/attendance/reports',
    ATTENDANCE_SCHEDULE: '/attendance/schedule',
    DEVICE_ATTENDANCE_LOGS: '/attendance/device-logs',

    // Timesheet Management
    TIMESHEET_READ: 'Timesheet:read',
    TIMESHEET_CREATE: 'Timesheet:create',
    TIMESHEET_UPDATE: 'Timesheet:update',
    TIMESHEET_APPROVE: 'Timesheet:approve',
    TIMESHEET_REJECT: 'Timesheet:reject',

    // Department routes
    DEPARTMENTS: '/departments',
    DEPARTMENT_CREATE: '/departments/create',
    DEPARTMENT_DETAILS: '/departments/:id',

    // Finance routes
    FINANCE: '/finance',
    BUDGETS: '/finance/budgets',
    ASSETS: '/finance/assets',
    REQUISITIONS: '/finance/requisitions',

    // Travel & Advances routes
    TRAVEL_ADVANCES: '/travel/advances',
    TRAVEL_REQUESTS: '/travel/requests',
    EXPENSE_REPORTS: '/travel/expense-reports',
    TRAVEL_ADVANCE_APPROVALS: '/travel/advance-approvals',
    TRAVEL_REQUEST_APPROVALS: '/travel/request-approvals',
    EXPENSE_REPORT_APPROVALS: '/travel/expense-approvals',

    // Document routes
    DOCUMENTS: '/documents',

    // Report routes
    REPORTS: '/reports',

    // Settings routes
    SETTINGS: '/settings',
    ROLES_SETTINGS: '/settings/roles',
    MENUS_SETTINGS: '/settings/menus',
    USERS_SETTINGS: '/settings/users',

    SETTINGS_AUDIT: '/settings/audit',
    SETTINGS_BACKUP: '/settings/backup',
    SETTINGS_COMPANY: '/settings/company',
    SETTINGS_EMAIL: '/settings/email',
    SETTINGS_NOTIFICATIONS: '/settings/notifications',
    SETTINGS_SECURITY: '/settings/security',
    SETTINGS_INTEGRATIONS: '/settings/integrations',

    // **ACTIVITY LOGS ROUTES**
    ACTIVITY_LOGS: '/settings/audit',
    AUDIT_TRAIL: '/audit/trail',
    SECURITY_LOGS: '/security/logs',

    // Performance routes
    PERFORMANCE: '/performance',
    PERFORMANCE_DASHBOARD: '/performance',
    PERFORMANCE_APPRAISALS: '/performance/appraisals',
    PERFORMANCE_APPRAISALS_MY: '/performance/appraisals/my',
    PERFORMANCE_APPRAISALS_PENDING: '/performance/appraisals/pending',
    PERFORMANCE_OBJECTIVES: '/performance/objectives',
    PERFORMANCE_OBJECTIVES_MY: '/performance/objectives/my',
    PERFORMANCE_OBJECTIVES_PENDING: '/performance/objectives/pending',

    // Profile routes
    PROFILE: '/profile',

    // Error routes
    NOT_FOUND: '/404',
    UNAUTHORIZED: '/unauthorized',
};

// Menu Icons (Material-UI icons) - Comprehensive unique icon mapping
export const MENU_ICONS = {
    // Main Navigation
    DASHBOARD: 'DashboardOutlined',
    HOME: 'HomeWorkOutlined',
    MONITOR: 'MonitorOutlined',

    // People & HR Management
    EMPLOYEES: 'PeopleOutlined',
    PROFILE: 'PersonOutlined',
    CONTACTS: 'ContactsOutlined',
    ADD_PERSON: 'PersonAddOutlined',
    MANAGE_ACCOUNTS: 'ManageAccountsOutlined',
    GROUPS: 'GroupOutlined',
    ADD_GROUP: 'GroupAddOutlined',
    BADGES: 'BadgeOutlined',
    WORK: 'WorkOutlined',

    // Time & Attendance
    ATTENDANCE: 'AccessTimeOutlined',
    LEAVES: 'EventNoteOutlined',
    CALENDAR: 'CalendarTodayOutlined',
    SCHEDULE: 'ScheduleOutlined',
    TIMELAPSE: 'TimelapseOutlined',
    EVENT_AVAILABLE: 'EventAvailableOutlined',
    EVENT_BUSY: 'EventBusyOutlined',

    // Departments & Organization
    DEPARTMENTS: 'CorporateFareOutlined',
    APARTMENT: 'ApartmentOutlined',
    CITY: 'LocationCityOutlined',
    BUSINESS_CENTER: 'BusinessCenterOutlined',

    // Finance & Accounting
    FINANCE: 'AccountBalanceOutlined',
    BUDGETS: 'AccountBalanceWalletOutlined',
    BUDGET_PLANNING: 'RequestQuoteOutlined',
    BUDGET_MONITORING: 'PieChartOutlined',
    BUDGET_VARIANCE: 'AssessmentOutlined',
    PETTY_CASH: 'AccountBalanceWalletOutlined',
    PETTY_CASH_BOOKS: 'MenuBookOutlined',
    PETTY_CASH_EXPENSES: 'ReceiptOutlined',
    PETTY_CASH_REPORTS: 'SummarizeOutlined',
    PAYMENTS: 'PaymentsOutlined',
    CREDIT_CARD: 'CreditCardOutlined',
    RECEIPTS: 'ReceiptOutlined',
    MONETIZATION: 'MonetizationOnOutlined',
    SAVINGS: 'SavingsOutlined',
    RECEIPT_LONG: 'ReceiptLongOutlined',
    TRAVEL_EXPENSES: 'FlightTakeoffOutlined',
    TRAVEL_REQUESTS: 'LuggageOutlined',
    TRAVEL_ADVANCE: 'AccountBalanceWalletOutlined',
    TRAVEL_REPORTS: 'AssignmentOutlined',

    // Documents & Files
    DOCUMENTS: 'FolderOutlined',
    DOCUMENT_SCANNER: 'DocumentScannerOutlined',
    DESCRIPTION: 'DescriptionOutlined',
    FILE_COPY: 'FileCopyOutlined',
    CLOUD_UPLOAD: 'CloudUploadOutlined',

    // Reports & Analytics
    REPORTS: 'AssessmentOutlined',
    BAR_CHART: 'BarChartOutlined',
    TRENDING_UP: 'TrendingUpOutlined',
    ANALYTICS: 'AnalyticsOutlined',
    INSIGHTS: 'InsightsOutlined',

    // Settings & Administration
    SETTINGS: 'SettingsOutlined',
    TUNE: 'TuneOutlined',
    BUILD: 'BuildOutlined',
    INTEGRATION: 'IntegrationInstructionsOutlined',
    ADMIN_PANEL: 'AdminPanelSettingsOutlined',

    // Security & Access
    ROLES: 'SecurityOutlined',
    USERS: 'SupervisedUserCircleOutlined',
    VPN_KEY: 'VpnKeyOutlined',
    SHIELD: 'ShieldOutlined',
    LOCK: 'LockOutlined',

    // Inventory & Assets
    ASSETS: 'InventoryOutlined',
    CATEGORY: 'CategoryOutlined',
    STORE: 'StoreOutlined',

    // Procurement & Purchasing
    REQUISITIONS: 'RequestQuoteOutlined',
    SHOPPING_CART: 'ShoppingCartOutlined',
    SHIPPING: 'LocalShippingOutlined',

    // Meetings & Events
    MEETINGS: 'MeetingRoomOutlined',
    TASKS: 'TaskOutlined',
    ASSIGNMENT: 'AssignmentTurnedInOutlined',

    // Travel & Expenses
    TRAVEL: 'FlightTakeoffOutlined',
    HOTEL: 'HotelOutlined',
    CAR: 'DirectionsCarOutlined',

    // Budget Management Submenu Icons
    BUDGET_PERIODS: 'AccountBalanceOutlined',
    CREATE_BUDGET: 'AddOutlined',
    VIEW_BUDGETS: 'ListOutlined',
    FINANCE_APPROVALS: 'ApprovalOutlined',
    ADMIN_APPROVALS: 'AdminPanelSettingsOutlined',

    // Performance Management
    PERFORMANCE: 'AssessmentOutlined',
    APPRAISALS: 'RateReviewOutlined',
    OBJECTIVES: 'FlagOutlined',
    REVIEWS: 'RateReviewOutlined',

    // General
    MENUS: 'SettingsOutlined',
};

// Leave Status Constants
export const LEAVE_STATUS = {
    DRAFT: 'draft',
    PENDING: 'pending',
    APPROVED_BY_SUPERVISOR: 'approved by supervisor',
    APPROVED_BY_HR: 'approved by hr',
    APPROVED: 'approved',
    REJECTED: 'rejected',
};

// Attendance Status Constants
export const ATTENDANCE_STATUS = {
    PRESENT: 'present',
    ABSENT: 'absent',
    ON_LEAVE: 'on leave',
    HALF_DAY: 'half day',
};

// File Upload Constants
export const FILE_UPLOAD = {
    MAX_SIZE: parseInt(import.meta.env.VITE_MAX_FILE_SIZE) || 10485760, // 10MB
    ALLOWED_TYPES: import.meta.env.VITE_ALLOWED_FILE_TYPES?.split(',') || [
        '.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.xls', '.xlsx'
    ],
};

// Theme Constants
export const THEME_CONSTANTS = {
    DRAWER_WIDTH: 280,
    HEADER_HEIGHT: 64,
    BREAKPOINTS: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
    },
};

// Pagination Constants
export const PAGINATION = {
    DEFAULT_PAGE_SIZE: 10,
    PAGE_SIZE_OPTIONS: [5, 10, 25, 50, 100],
};

// Date Format Constants
export const DATE_FORMATS = {
    DISPLAY: 'dd/MM/yyyy',
    API: 'yyyy-MM-dd',
    DATETIME_DISPLAY: 'dd/MM/yyyy HH:mm',
    TIME_DISPLAY: 'HH:mm',
};

// Notification Types
export const NOTIFICATION_TYPES = {
    SUCCESS: 'success',
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info',
};

export const MEETING_PERMISSIONS = {
    VIEW_MEETINGS: 'view_meetings',
    CREATE_MEETINGS: 'create_meetings',
    UPDATE_MEETINGS: 'update_meetings',
    DELETE_MEETINGS: 'delete_meetings',
    MANAGE_MEETING_ATTENDEES: 'manage_meeting_attendees',
    UPLOAD_MEETING_MINUTES: 'upload_meeting_minutes',
    CREATE_MEETING_TASKS: 'create_meeting_tasks',
    UPDATE_TASK_STATUS: 'update_task_status',
    VIEW_MEETING_REPORTS: 'view_meeting_reports',
};